import type { Page } from '@hello-aria/makalu-api-client'
import type { InfiniteData, UseInfiniteQueryResult, UseMutationResult } from '@tanstack/react-query'
import { keepPreviousData, useInfiniteQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { deleteUser, listUsers, createUser } from '@/modules/users/services/userServices'
import type { QueryStringInput } from '@hello-aria/query-string/dist/stringify'
import type { DeleteUser, PostUser, User } from '@/shared/models/userModel'
import { getPageSize } from '@/shared/utils/queryUtils'
import { useMe } from './useMe'

export const usersQueryKeys = {
    root: ['infiniteUsers'],
    users: ['users'],
    infiniteUsers: (companyId: string, query?: QueryStringInput): (string | undefined | QueryStringInput)[] => [
        ...usersQueryKeys.root,
        companyId,
        query
    ]
}

interface UseInfiniteUsers {
    query?: QueryStringInput
    enabled?: boolean
    keepPreviousData?: boolean
}

export const useInfiniteUsers = (
    options?: UseInfiniteUsers
): UseInfiniteQueryResult<InfiniteData<Page<User> | undefined>> => {
    const { query, enabled, keepPreviousData: shouldKeepPreviousData } = options || {}
    const { companyId } = useMe()

    return useInfiniteQuery({
        queryKey: usersQueryKeys.infiniteUsers(companyId, query),
        queryFn: async ({ pageParam }: { pageParam?: string }) => {
            return listUsers({
                companyId,
                query: {
                    ...query,
                    pagination: {
                        after: pageParam,
                        first: getPageSize(query)
                    }
                }
            })
        },
        enabled,
        placeholderData: shouldKeepPreviousData ? keepPreviousData : undefined,
        getNextPageParam: (lastPage) => {
            if (lastPage?.pagination?.hasNextPage) return lastPage?.pagination?.endCursor
            return undefined
        },

        initialPageParam: undefined
    })
}

export const usePostUser = (): UseMutationResult<User | undefined, unknown, PostUser> => {
    const queryClient = useQueryClient()
    const { companyId } = useMe()

    return useMutation({
        mutationFn: (payload: PostUser) => createUser(payload, companyId),
        onSuccess: () => queryClient.invalidateQueries({ queryKey: usersQueryKeys.root })
    })
}

export const useDeleteUser = (): UseMutationResult<User | undefined, unknown, DeleteUser> => {
    const { companyId } = useMe()

    return useMutation({
        mutationFn: (payload: DeleteUser) => deleteUser(companyId, payload)
    })
}
